import React, { useState, useEffect } from 'react';
import { Spinner } from "react-bootstrap";
import axios from 'axios';
import Swal from "sweetalert2";
import { MyProSidebarProvider } from "../components/SidebarContext";
import { PaystackConsumer } from 'react-paystack';
import HomeNav from '../components/Navbar';
import Footer from '../components/Footer';
import '../App.css';
import { Link } from 'react-router-dom';
let date = new Date();

// Environment variables (should be in .env file)
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://your-api-url.com';
const PAYSTACK_URL = process.env.REACT_APP_PAYSTACK_URL || 'https://api.paystack.co';
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_your_public_key';

const Plans = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMonthly, setMonthlyLoading] = useState(false);
  const [loadingSixMonths, setSixMonthsLoading] = useState(false);
  const [loadingQuarterly, setQuarterlyLoading] = useState(false);
  const [loadingYearly, setYearlyLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly Explorer',
      price: 5000,
      period: 'month',
      originalPrice: 5000,
      features: [
        '✅ Full access to all courses',
        '✅ 24/7 expert support',
        '✅ Downloadable resources',
        '✅ Progress tracking',
        '✅ Certificate of completion',
        '❌ Premium community access',
        '❌ Personal mentor'
      ],
      popular: false,
      savings: 0,
      planCode: 'PLN_cvrc4zftj167nsq',
      cta: "Start Learning"
    },
    {
      id: 'six-months',
      name: 'Six-Month Accelerator',
      price: 25000,
      period: '6 months',
      originalPrice: 30000,
      features: [
        '✅ Full access to all courses',
        '✅ 24/7 priority support',
        '✅ Downloadable resources + templates',
        '✅ Advanced progress analytics',
        '✅ Certificate of completion',
        '✅ Premium community access',
        '❌ Personal mentor'
      ],
      popular: true,
      savings: 17,
      planCode: 'PLN_six_months_accelerator',
      cta: "Accelerate Your Growth"
    },
    {
      id: 'yearly',
      name: 'Annual Mastery',
      price: 35000,
      period: 'year',
      originalPrice: 60000,
      features: [
        '✅ Full access to all courses + future releases',
        '✅ 24/7 VIP support',
        '✅ All resources + premium templates',
        '✅ Advanced analytics + career guidance',
        '✅ Certificate of completion + badges',
        '✅ Premium community access',
        '✅ Dedicated personal mentor'
      ],
      popular: false,
      savings: 42,
      planCode: 'PLN_nuzete9o6st8i0e',
      cta: "Master Your Skills"
    }
  ];

  // REMOVED: useEffect that checked email verification on page load

  const checkEmailVerification = async () => {
    const token = localStorage.getItem("codefussion-login-token");
    if (!token) return { verified: false, error: "Not logged in" };
    
    try {
      const userData = { email: localStorage.getItem("codefussion-student-email") };
      const response = await axios.post(`${BASE_URL}check-email-verification.php`, userData, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: token
        }
      });
      
      if (response.data.status === 1 && response.data.length > 0) {
        const verifyStatus = response.data[0].verify_status;
        return { verified: verifyStatus === "true", error: null };
      }
      return { verified: false, error: "Verification status not found" };
    } catch (error) {
      console.error("Email verification check failed:", error);
      return { verified: false, error: "Failed to check email verification" };
    }
  };

  const handleSubscription = async (plan) => {
    // Check if user is logged in
    if (!localStorage.getItem("codefussion-login-token")) {
      Swal.fire({
        title: "Login Required",
        text: "Please log in to subscribe to a plan",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Login Now",
        cancelButtonText: "Cancel",
        backdrop: `rgba(143, 70, 0, 0.3)`
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.replace("/login");
        }
      });
      return;
    }

    // Check email verification only when subscribing
    setSelectedPlan(plan.id);
    
    if (plan.id === 'monthly') setMonthlyLoading(true);
    else if (plan.id === 'six-months') setSixMonthsLoading(true);
    else if (plan.id === 'yearly') setYearlyLoading(true);

    try {
      // Check email verification status
      const verificationResult = await checkEmailVerification();
      
      if (!verificationResult.verified) {
        Swal.fire({
          title: "Email Not Verified",
          text: "Please verify your email to continue with subscription",
          icon: "error",
          confirmButtonText: "OK",
          backdrop: `rgba(143, 70, 0, 0.3)`
        });
        return;
      }

      // Proceed with subscription if email is verified
      const userData = {
        email: localStorage.getItem("codefussion-student-email"),
        amount: plan.price * 100, // Convert to kobo
        plan: plan.planCode,
        callback_url: `${window.location.origin}/verify-payment`
      };

      const response = await axios.post(`${PAYSTACK_URL}/transaction/initialize`, userData, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_SECRET_KEY}`
        }
      });

      if (response.data.status && response.data.data.authorization_url) {
        localStorage.setItem("codefussion-payment-reference", response.data.data.reference);
        localStorage.setItem("codefussion-payment-code", response.data.data.access_code);
        window.location.href = response.data.data.authorization_url;
      }
    } catch (error) {
      console.error("Subscription error:", error);
      Swal.fire({
        title: "Error",
        text: "An error occurred while processing your subscription. Please try again.",
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      if (plan.id === 'monthly') setMonthlyLoading(false);
      else if (plan.id === 'six-months') setSixMonthsLoading(false);
      else if (plan.id === 'yearly') setYearlyLoading(false);
      setSelectedPlan(null);
    }
  };

  // REMOVED: Loading check that was showing spinner on page load

  return (
    <div className="premium-subscription-page">
      <HomeNav />
      
      {/* Hero Section */}
      <section className="subscription-hero">
        <div className="hero-content">
          <div className="hero-badge">LIMITED TIME OFFER</div>
          <h1>Unlock Your <span className="gradient-text">Full Potential</span></h1>
          <p className="hero-subtitle">
            Join thousands of successful learners who transformed their careers. 
            <strong> Start your journey to mastery today!</strong>
          </p>
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-number">10,000+</div>
              <div className="stat-label">Successful Students</div>
            </div>
            <div className="stat">
              <div className="stat-number">98%</div>
              <div className="stat-label">Satisfaction Rate</div>
            </div>
            <div className="stat">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Expert Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="plans-section">
        <div className="container">
          <div className="section-header">
            <h2>Choose Your Success Path</h2>
            <p>Select the plan that matches your ambition and learning style</p>
          </div>

          <div className="plans-comparison">
            <div className="plans-container">
              {plans.map(plan => (
                <div 
                  key={plan.id} 
                  className={`plan-card ${plan.popular ? 'popular featured' : ''}`}
                  data-aos="fade-up"
                  data-aos-delay={plan.popular ? 200 : 100}
                >
                  {plan.popular && (
                    <div className="popular-badge">
                      <span>🔥 MOST POPULAR</span>
                    </div>
                  )}

                  {plan.savings > 0 && (
                    <div className="savings-badge">
                      Save {plan.savings}%
                    </div>
                  )}

                  <div className="plan-header">
                    <h3>{plan.name}</h3>
                    <div className="price-section">
                      <div className="price">
                        <span className="currency">₦</span>
                        <span className="amount">{plan.price.toLocaleString()}</span>
                        <span className="period">/{plan.period}</span>
                      </div>
                      {plan.originalPrice > plan.price && (
                        <div className="original-price">
                          ₦{plan.originalPrice.toLocaleString()}
                        </div>
                      )}
                      <div className="effective-price">
                        {plan.id === 'monthly' && '₦5,000 per month'}
                        {plan.id === 'six-months' && 'Only ₦4,167 per month'}
                        {plan.id === 'yearly' && 'Only ₦2,917 per month'}
                      </div>
                    </div>
                  </div>

                  <div className="plan-features">
                    <ul>
                      {plan.features.map((feature, index) => (
                        <li key={index} className={feature.startsWith('❌') ? 'disabled' : ''}>
                          {feature.startsWith('✅') ? (
                            <span className="feature-icon">✓</span>
                          ) : (
                            <span className="feature-icon disabled">✗</span>
                          )}
                          {feature.replace('✅ ', '').replace('❌ ', '')}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="plan-action">
                    <button 
                      onClick={() => handleSubscription(plan)}
                      disabled={
                        (plan.id === 'monthly' && loadingMonthly) ||
                        (plan.id === 'six-months' && loadingSixMonths) ||
                        (plan.id === 'yearly' && loadingYearly)
                      }
                      className={`subscribe-btn ${plan.popular ? 'btn-featured' : ''}`}
                    >
                      {selectedPlan === plan.id ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          {plan.cta}
                          <span className="btn-arrow">→</span>
                        </>
                      )}
                    </button>
                    <p className="plan-guarantee">7-day money-back guarantee • Cancel anytime</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Value Proposition */}
          <div className="value-proposition">
            <div className="value-grid">
              <div className="value-item">
                <div className="value-icon">🚀</div>
                <h4>Accelerate Your Career</h4>
                <p>Gain in-demand skills that employers are looking for</p>
              </div>
              <div className="value-item">
                <div className="value-icon">💼</div>
                <h4>Job-Ready Skills</h4>
                <p>Learn practical skills you can apply immediately</p>
              </div>
              <div className="value-item">
                <div className="value-icon">🏆</div>
                <h4>Industry Recognition</h4>
                <p>Get certified and stand out to employers</p>
              </div>
              <div className="value-item">
                <div className="value-icon">👥</div>
                <h4>Community Support</h4>
                <p>Join a network of ambitious learners</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2>Success Stories from Our Students</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                "CodeFussion transformed my career! I went from beginner to hired in 6 months."
              </div>
              <div className="testimonial-author">
                <strong>Sarah M.</strong>
                <span>Full Stack Developer</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                "The projects and community support made all the difference. Worth every penny!"
              </div>
              <div className="testimonial-author">
                <strong>James K.</strong>
                <span>Frontend Engineer</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                "I tried free resources for years. CodeFussion's structured approach got me results."
              </div>
              <div className="testimonial-author">
                <strong>Amanda T.</strong>
                <span>Software Developer</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I cancel my subscription anytime?</h4>
              <p>Yes! You can cancel your subscription at any time. Your access will continue until the end of your billing period.</p>
            </div>
            <div className="faq-item">
              <h4>Do you offer a money-back guarantee?</h4>
              <p>Absolutely! We offer a 7-day money-back guarantee if you're not satisfied with your learning experience.</p>
            </div>
            <div className="faq-item">
              <h4>Will I receive a certificate?</h4>
              <p>Yes, all paid plans include certificates of completion for each course you finish.</p>
            </div>
            <div className="faq-item">
              <h4>How often is new content added?</h4>
              <p>We add new courses and update existing content monthly to keep up with industry trends.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Transform Your Future?</h2>
          <p>Join thousands of learners who've already started their journey to success</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={() => document.querySelector('.plans-section').scrollIntoView({ behavior: 'smooth' })}>
              Choose Your Plan Now
            </button>
            <button className="cta-secondary">
              Speak with an Advisor
            </button>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .premium-subscription-page {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
        }

        /* Hero Section */
        .subscription-hero {
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          backdrop-filter: blur(10px);
          padding: 100px 0 80px;
          text-align: center;
          color: white;
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .hero-badge {
          background: linear-gradient(45deg, #FF6B6B, #FF8E53);
          padding: 8px 20px;
          border-radius: 25px;
          font-weight: 700;
          font-size: 0.9rem;
          display: inline-block;
          margin-bottom: 20px;
          animation: pulse 2s infinite;
        }

        .subscription-hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          margin-bottom: 20px;
          line-height: 1.1;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FFA500);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          margin-bottom: 40px;
          opacity: 0.9;
          line-height: 1.6;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 60px;
          margin-top: 50px;
        }

        .stat {
          text-align: center;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: #FFD700;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.8;
        }

        /* Plans Section */
        .plans-section {
          padding: 100px 0;
          background: white;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 15px;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        .plans-comparison {
          margin-bottom: 80px;
        }

        .plans-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .plan-card {
          background: white;
          border-radius: 20px;
          padding: 40px 30px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          border: 2px solid #f0f0f0;
          position: relative;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .plan-card.featured {
          border-color: #FFD700;
          transform: scale(1.05);
          box-shadow: 0 20px 60px rgba(255, 215, 0, 0.2);
        }

        .plan-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 60px rgba(0,0,0,0.15);
        }

        .plan-card.featured:hover {
          transform: scale(1.05) translateY(-10px);
        }

        .popular-badge {
          position: absolute;
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(45deg, #FF6B6B, #FF8E53);
          color: white;
          padding: 10px 25px;
          border-radius: 25px;
          font-weight: 700;
          font-size: 0.8rem;
          white-space: nowrap;
        }

        .savings-badge {
          position: absolute;
          top: 20px;
          right: 20px;
          background: #00cc88;
          color: white;
          padding: 5px 12px;
          border-radius: 15px;
          font-weight: 700;
          font-size: 0.8rem;
        }

        .plan-header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .plan-header h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 15px;
        }

        .price-section {
          margin-top: 15px;
        }

        .price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 5px;
          margin-bottom: 10px;
        }

        .currency {
          font-size: 1.2rem;
          font-weight: 600;
          color: #1a1a1a;
        }

        .amount {
          font-size: 3rem;
          font-weight: 800;
          color: #0066ff;
          line-height: 1;
        }

        .period {
          font-size: 1.2rem;
          color: #666;
          font-weight: 600;
        }

        .original-price {
          text-decoration: line-through;
          color: #999;
          font-size: 1.1rem;
          margin-bottom: 5px;
        }

        .effective-price {
          color: #00cc88;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .plan-features {
          flex: 1;
          margin-bottom: 30px;
        }

        .plan-features ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .plan-features li {
          display: flex;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f8f9fa;
        }

        .plan-features li.disabled {
          color: #999;
        }

        .feature-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #00cc88;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 700;
          margin-right: 15px;
          flex-shrink: 0;
        }

        .feature-icon.disabled {
          background: #e0e0e0;
          color: #999;
        }

        .plan-action {
          text-align: center;
        }

        .subscribe-btn {
          width: 100%;
          padding: 18px 30px;
          border: none;
          border-radius: 15px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 15px;
        }

        .subscribe-btn:not(.btn-featured) {
          background: #0066ff;
          color: white;
        }

        .subscribe-btn:not(.btn-featured):hover {
          background: #0052cc;
          transform: translateY(-2px);
        }

        .btn-featured {
          background: linear-gradient(45deg, #FFD700, #FFA500);
          color: #000;
          font-weight: 800;
        }

        .btn-featured:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(255, 215, 0, 0.4);
        }

        .btn-arrow {
          font-size: 1.2rem;
          transition: transform 0.3s ease;
        }

        .subscribe-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        .plan-guarantee {
          font-size: 0.9rem;
          color: #666;
          margin: 0;
        }

        /* Value Proposition */
        .value-proposition {
          margin-top: 80px;
        }

        .value-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 40px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .value-item {
          text-align: center;
          padding: 30px 20px;
        }

        .value-icon {
          font-size: 3rem;
          margin-bottom: 20px;
        }

        .value-item h4 {
          font-size: 1.3rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #1a1a1a;
        }

        .value-item p {
          color: #666;
          line-height: 1.6;
        }

        /* Testimonials */
        .testimonials-section {
          padding: 100px 0;
          background: #f8f9fa;
        }

        .testimonials-section h2 {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 60px;
          color: #1a1a1a;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .testimonial-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        .testimonial-content {
          font-size: 1.1rem;
          line-height: 1.6;
          color: #333;
          margin-bottom: 20px;
          font-style: italic;
        }

        .testimonial-author {
          display: flex;
          flex-direction: column;
        }

        .testimonial-author strong {
          color: #1a1a1a;
          margin-bottom: 5px;
        }

        .testimonial-author span {
          color: #666;
          font-size: 0.9rem;
        }

        /* FAQ Section */
        .faq-section {
          padding: 100px 0;
          background: white;
        }

        .faq-section h2 {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 60px;
          color: #1a1a1a;
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 40px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .faq-item h4 {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #1a1a1a;
        }

        .faq-item p {
          color: #666;
          line-height: 1.6;
        }

        /* Final CTA */
        .final-cta {
          padding: 100px 0;
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
          color: white;
          text-align: center;
        }

        .final-cta h2 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 20px;
        }

        .final-cta p {
          font-size: 1.2rem;
          margin-bottom: 40px;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-primary {
          background: linear-gradient(45deg, #FFD700, #FFA500);
          color: #000;
          border: none;
          padding: 18px 35px;
          border-radius: 15px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cta-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
        }

        .cta-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
          padding: 18px 35px;
          border-radius: 15px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cta-secondary:hover {
          background: white;
          color: #1a1a1a;
        }

        /* Animations */
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .subscription-hero h1 {
            font-size: 2.5rem;
          }

          .hero-stats {
            gap: 30px;
            flex-wrap: wrap;
          }

          .plans-container {
            grid-template-columns: 1fr;
          }

          .plan-card.featured {
            transform: none;
          }

          .plan-card.featured:hover {
            transform: translateY(-10px);
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-primary, .cta-secondary {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 480px) {
          .subscription-hero h1 {
            font-size: 2rem;
          }

          .section-header h2 {
            font-size: 2rem;
          }

          .amount {
            font-size: 2.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Plans;